
import { defineComponent, ref, watchEffect, inject, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { Apollo, Notify } from '@/core/services';
import { GET_CATEGORIES, GET_CATEGORIES_DROPDOWN_LIST, SEARCH_CATEGORIES } from '@/modules/catalog/categories/graphql/Queries';
import { DELETE_CATEGORY, UPDATE_CATEGORY_POSITION } from '@/modules/catalog/categories/graphql/Mutations';
import Search from '../../../../components/search/Search.vue';
import Table from '../../../../components/Table/Table.vue';
import InnerLoader from '../../../../components/InnerLoader.vue';
import { dropdown_handler } from '../../../../core/helpers/dropdownHandler';
import { setPageHistory } from '@/core/helpers/toolbar';
import { useRouter } from 'vue-router';
import { wmsRecordReadOnly } from '@/modules/wms/helpers';
import WmsSourceBadge from '@/modules/wms/components/WmsSourceBadge.vue';

export default defineComponent({
    name: 'categories-list',
    components: { Search, Table, InnerLoader, WmsSourceBadge },
    setup() {
        const i18n = useI18n();
        const store = useStore();
        const loader = ref(false);
        const loading = ref(false);
        const categoriesData: any = ref([]);
        const pagination = ref({}) as Record<any, any>;
        const system_locale = ref();
        const emitter: any = inject('emitter');
        const clickHandler = ref() as Record<any, any>;
        const currentPage = ref();
        const permissions = store.getters.getPermissions;
        const activeIndex = ref(-1);
        const searchObservable: any = ref(null);
        const observable: any = ref(null);
        const router = useRouter();
        const columns = ref([
            {
                label: 'message.NAME',
                key: 'name'
            },
            {
                label: 'message.DESCRIPTION',
                key: 'description'
            },
            {
                label: 'message.PARENT',
                key: 'parent'
            },
            {
                label: 'message.STATUS',
                key: 'status'
            },
            {
                // Smart WMS or the shop admin (WMS module)
                label: 'message.MANAGED_BY',
                key: 'managed_by'
            },
            {
                label: 'message.POSITION',
                key: 'position'
            },
            {
                label: 'message.ACTIONS',
                key: 'actions',
                textAlignment: 'center'
            }
        ]);

        const filterOBject = ref([
            {
                label: 'message.NAME',
                value: 'name'
            }
        ]);

        watchEffect(() => {
            system_locale.value = i18n.fallbackLocale.value;
        });

        const dropdownHandler = (index, id) => {
            activeIndex.value = index === id ? null : id;
        };
        const handleClickOutside = event => {
            if (!event.target.closest('.action-btn')) {
                activeIndex.value = -1;
            }
        };

        const get_categories = (page = 1, reload = false) => {
            if (searchObservable.value != null) {
                searchObservable.value?.unsubscribe();
                searchObservable.value = null;
            }
            if (page == 1) observable.value?.unsubscribe();
            loader.value = true;
            observable.value = Apollo.watchQuery({
                query: GET_CATEGORIES,
                errorPolicy: 'all',
                fetchPolicy: 'cache-and-network',
                nextFetchPolicy: 'cache-only',
                variables: {
                    page: page,
                    limit: 10
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                const searchData = store.getters.getSearchData;
                if (Object.entries(searchData).length == 0) {
                    categoriesData.value = [];
                    reload ? (currentPage.value = 1) : false;
                    clickHandler.value = get_categories;
                    categoriesData.value = data?.categories?.data;
                    pagination.value = data.categories;
                }

                loader.value = false;
            });
        };

        const handleCurrentChange = (num: number) => {
            clickHandler.value(num);
        };

        const searchHandler = (page = 1) => {
            if (observable.value != null) {
                observable.value?.unsubscribe();
                observable.value = null;
            }
            searchObservable.value?.unsubscribe();
            const searchData = store.getters.getSearchData;
            loader.value = true;
            searchObservable.value = Apollo.watchQuery({
                query: SEARCH_CATEGORIES,
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'network-only',
                errorPolicy: 'all',
                variables: {
                    page: page,
                    limit: 10,
                    filter: searchData.data.select,
                    search_key: searchData.data.input,
                    locale: system_locale.value
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                categoriesData.value = [];
                clickHandler.value = searchHandler;
                categoriesData.value = data.search_categories.data;
                pagination.value = data.search_categories;
                loader.value = false;
            });
        };

        const handleDelete = (id: number) => {
            store.getters.appInstance.$messageBox
                .confirm(`${i18n.t('message.ARE_YOU_SURE_TO_DELETE_RECORD')}?`, i18n.t('message.INFO'), {
                    confirmButtonText: i18n.t('message.OK'),
                    cancelButtonText: i18n.t('message.CANCEL'),
                    type: 'info'
                })
                .then(async () => {
                    loader.value = true;
                    await Apollo.mutate({
                        mutation: DELETE_CATEGORY,
                        variables: { id: id },
                        update: (store, { data: { delete_category } }) => {
                            const pervious_record = store.readQuery({
                                query: GET_CATEGORIES,
                                variables: {
                                    page: pagination.value.current_page,
                                    limit: 10
                                }
                            }) as Record<any, any>;
                            const data = pervious_record.categories.data.filter((t: any) => t.id !== delete_category?.id);

                            store.writeQuery({
                                query: GET_CATEGORIES,
                                data: {
                                    categories: {
                                        ...pervious_record.categories,
                                        data: [...data]
                                    }
                                },
                                variables: {
                                    page: pagination.value.current_page,
                                    limit: 10
                                }
                            });

                            const pervious_dropdown_record = store.readQuery({
                                query: GET_CATEGORIES_DROPDOWN_LIST
                            }) as Record<any, any>;
                            const dropdown_data = pervious_dropdown_record.categories.data.filter((t: any) => t.id !== delete_category?.id);
                            store.writeQuery({
                                query: GET_CATEGORIES_DROPDOWN_LIST,
                                data: {
                                    categories: {
                                        ...pervious_dropdown_record.categories,
                                        data: [...dropdown_data]
                                    }
                                }
                            });

                            loader.value = false;
                        }
                    });
                    Notify.success(i18n.t('message.RECORD_DELETED_SUCCESSFULLY'));
                })
                .catch((e: Record<any, any>) => {
                    loader.value = false;
                });
        };

        const updatePosition = (position: number, new_position: number, category_id: number) => {
            // view-only while WMS manages products, or for a category from Smart
            if (wmsRecordReadOnly(categoriesData.value.find(category => category.id == category_id))) {
                return;
            }
            if (new_position <= 0) {
                Notify.error(i18n.t('message.POSITION_MUST_BE_GREATER_THAN_0'));
                return;
            }
            if (position == new_position) {
                return;
            }
            const formData = {
                old_position: position,
                new_position: new_position
            };
            // const searchData = store.getters.getSearchData;
            loader.value = true;
            Apollo.mutate({
                mutation: UPDATE_CATEGORY_POSITION,
                variables: {
                    input: formData
                },
                update: (store, { data: { update_category_position } }) => {
                    loader.value = false;
                    if (update_category_position == true) {
                        if (searchObservable.value != null) {
                            searchObservable.value.unsubscribe();
                            searchHandler(pagination.value.current_page);
                        } else {
                            get_categories(pagination.value.current_page, true);
                        }
                        Notify.success(`${i18n.t('message.POSITION_CHANGED_SUCCESSFULLY')}`);
                    }
                }
            }).catch(e => {
                console.log(e);
                loader.value = false;
            });
        };

        onMounted(() => {
            get_categories(1, true);
            document.addEventListener('click', handleClickOutside);
        });

        const handleEdit = (category: Record<any, any>) => {
            // category = ({...category, current_page: pagination.value.current_page});
            emitter.emit('editCategoryAction', category);
        };

        const exportFile = data => {
            const searchData = store.getters.getSearchData;
            loading.value = true;
            const sub = Apollo.watchQuery({
                query: SEARCH_CATEGORIES,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                variables: {
                    page: 0,
                    limit: 1,
                    filter: searchData?.data?.select ? searchData?.data?.select : '',
                    search_key: searchData?.data?.input ? searchData?.data?.input : '',
                    locale: system_locale.value,
                    type: data?.type,
                    date_from: data?.dateFrom,
                    date_to: data?.dateTo
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loading.value = false;
                } else if (data) {
                    window.open(data?.search_categories?.data[0]?.path, '_blank');
                    loading.value = false;
                }
                loading.value = false;
                sub.unsubscribe();
            });
        };

        const capitalizeFirstLetter = category => {
            const lowercasedText = category.toLowerCase();
            return lowercasedText.charAt(0).toUpperCase() + lowercasedText.slice(1);
        };

        const handleProductSorting = (category: Record<any, any>) => {
            const searchData = store.getters.getSearchData;
            setPageHistory({
                filter: searchData?.data?.select,
                search_key: searchData?.data?.input,
                page_number: pagination.value?.current_page
            });
            localStorage.setItem('slug', category.slug);
            router.push({ name: 'categor_products_sorting', params: { slug: category.slug } });
        };

        onUnmounted(() => {
            observable.value?.unsubscribe();
            searchObservable.value?.unsubscribe();
        });

        return {
            pagination,
            filterOBject,
            system_locale,
            categoriesData,
            currentPage,
            permissions,
            columns,
            loading,
            loader,
            activeIndex,
            capitalizeFirstLetter,
            exportFile,
            searchHandler,
            handleCurrentChange,
            get_categories,
            handleEdit,
            handleDelete,
            updatePosition,
            dropdownHandler,
            handleClickOutside,
            handleProductSorting,
            wmsRecordReadOnly
        };
    }
});
